import { z } from "zod";
import { CHANNEL_KEYS } from "../channels.js";

export const ORDER_STATUSES = ["new", "awaiting_payment", "approved", "preparing", "packed", "shipped", "delivered", "cancelled", "returned", "problem"] as const;
export type OrderStatusKey = (typeof ORDER_STATUSES)[number];

export const ORDER_STATUS_LABELS: Record<OrderStatusKey, string> = {
  new: "Yeni",
  awaiting_payment: "Ödeme bekliyor",
  approved: "Onaylandı",
  preparing: "Hazırlanıyor",
  packed: "Paketlendi",
  shipped: "Kargoda",
  delivered: "Teslim edildi",
  cancelled: "İptal",
  returned: "İade",
  problem: "Sorunlu",
};

export const ORDER_SORTS = ["orderedAt_desc", "orderedAt_asc", "grandTotal_desc", "grandTotal_asc"] as const;

export const ordersQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(10).max(100).default(25),
  status: z.enum(ORDER_STATUSES).optional(),
  /** Birden çok durum (Kargo / İade ekranlarının sekmeleri): "approved,preparing". */
  statuses: z
    .string()
    .max(200)
    .optional()
    .transform((v) => (v ? v.split(",").filter((s): s is (typeof ORDER_STATUSES)[number] => (ORDER_STATUSES as readonly string[]).includes(s)) : undefined)),
  /** Durum sekmesi sayıları yalnız bu durumlar içinde hesaplanır. */
  scope: z
    .string()
    .max(200)
    .optional()
    .transform((v) => (v ? v.split(",").filter((s): s is (typeof ORDER_STATUSES)[number] => (ORDER_STATUSES as readonly string[]).includes(s)) : undefined)),
  channelAccountId: z.string().max(40).optional(),
  /** Kanal türü (tüm IdeaSoft veya tüm Trendyol hesapları). */
  channel: z.enum(CHANNEL_KEYS).optional(),
  /** Siparişte bu markadan en az bir ürün olanlar. */
  brand: z
    .preprocess((v) => (v === undefined || v === "" ? undefined : Array.isArray(v) ? v : [v]), z.array(z.string().trim().min(1).max(120)).max(200))
    .optional(),
  /** İade talebi olan siparişler: bekleyen, onaylanan veya hepsi. */
  returns: z.enum(["requested", "approved", "any"]).optional(),
  q: z.string().trim().max(100).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  sort: z.enum(ORDER_SORTS).default("orderedAt_desc"),
});
export type OrdersQuery = z.infer<typeof ordersQuerySchema>;

/** Panelden sipariş işlemi (kanala yazar). */
export const ORDER_ACTIONS = ["start_picking", "mark_invoiced", "approve", "prepare", "ship", "deliver", "set_status"] as const;

/** IdeaSoft'ta panelden seçilebilen durumlar (silme ve iade hariç; iade, iade talebinden yürür). */
export const IDEASOFT_SETTABLE_STATUSES = [
  "waiting_for_approval",
  "waiting_for_payment",
  "approved",
  "being_prepared",
  "on_accumulation",
  "fulfilled",
  "delivered",
  "cancelled",
] as const;
export type OrderActionKey = (typeof ORDER_ACTIONS)[number];

export const orderActionSchema = z
  .object({
    action: z.enum(ORDER_ACTIONS),
    invoiceNumber: z.string().trim().min(3, "Fatura numarası girin").max(64).optional(),
    trackingNumber: z.string().trim().min(5, "Takip numarası girin").max(64).optional(),
    carrierName: z.string().trim().max(120).optional(),
    /** set_status: kanaldaki hedef durum (IdeaSoft). */
    targetStatus: z.enum(IDEASOFT_SETTABLE_STATUSES).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.action === "mark_invoiced" && !v.invoiceNumber) ctx.addIssue({ code: "custom", path: ["invoiceNumber"], message: "Fatura numarası gerekli" });
    if (v.action === "ship" && !v.trackingNumber) ctx.addIssue({ code: "custom", path: ["trackingNumber"], message: "Takip numarası gerekli" });
    if (v.action === "set_status" && !v.targetStatus) ctx.addIssue({ code: "custom", path: ["targetStatus"], message: "Durum seçin" });
  });
export type OrderActionInput = z.infer<typeof orderActionSchema>;

export interface OrderActionDto {
  key: OrderActionKey;
  label: string;
  hint: string;
  /** İşlem için istenecek bilgi. */
  needs: "invoiceNumber" | "tracking" | null;
}

/** Kanal ham durum adlarının Türkçesi (durum geçmişinde). */
export const EXTERNAL_STATUS_TR: Record<string, string> = {
  Created: "Yeni",
  Awaiting: "Ödeme bekliyor",
  Verified: "Onaylandı",
  Picking: "Toplanıyor",
  Invoiced: "Faturalandı",
  Shipped: "Kargoda",
  AtCollectionPoint: "Teslim noktasında",
  Delivered: "Teslim edildi",
  UnDelivered: "Teslim edilemedi",
  Cancelled: "İptal",
  UnSupplied: "Tedarik edilemedi",
  UnPacked: "Paket bölündü",
  Repack: "Yeniden paketleniyor",
  Returned: "İade",
  waiting_for_approval: "Onay bekliyor",
  waiting_for_payment: "Ödeme bekliyor",
  approved: "Onaylandı",
  being_prepared: "Hazırlanıyor",
  on_accumulation: "Tedarik sürecinde",
  fulfilled: "Kargoya verildi",
  delivered: "Teslim edildi",
  cancelled: "İptal",
  refunded: "İade edildi",
  deleted: "Silindi",
  failed: "Ödeme başarısız",
  in_transaction: "Ödeme işlemde",
  personal_status_1: "Özel durum 1",
  personal_status_2: "Özel durum 2",
  personal_status_3: "Özel durum 3",
};
export const externalStatusLabel = (s: string | null | undefined) => (s ? (EXTERNAL_STATUS_TR[s] ?? s) : "");

/** IdeaSoft ödeme işlem durumu. */
export const PAYMENT_STATUS_TR: Record<string, string> = { success: "ödendi", failed: "başarısız", in_transaction: "işlemde" };

/**
 * Mağazaya özel / tanınmayan kanal durumlarının (ör. IdeaSoft "Özel durum 1") dahili karşılığı.
 * Varsayılan "Sorunlu": ciroya girmez. Kullanıcı gerçek anlamını seçince satış/iptal sayılır.
 */
export const STATUS_MAP_TARGETS = ["problem", "new", "awaiting_payment", "approved", "preparing", "packed", "shipped", "delivered", "cancelled", "returned"] as const satisfies readonly OrderStatusKey[];
export const statusMapSchema = z.object({
  items: z
    .array(
      z.object({
        externalStatus: z.string().trim().min(1).max(64),
        target: z.enum(STATUS_MAP_TARGETS),
        name: z.string().trim().max(60).default(""),
      }),
    )
    .max(30),
});
export type StatusMapInput = z.input<typeof statusMapSchema>;

export interface StatusMapItemDto {
  externalStatus: string;
  label: string;
  name: string;
  target: OrderStatusKey;
  orders: number;
  revenue: string;
  lastOrderAt: string | null;
}
export interface StatusMapResponse {
  items: StatusMapItemDto[];
  canManage: boolean;
}
